import { useReferralAppDispatch } from '../../../../hooks';
import { useDeviceType, useToast } from '@cian/ui-kit';
import { useSelector } from 'react-redux';
import { selectRegionsList } from '../../../../selectors/raferral/form';
import { getReferralUserPhone } from '../../../../selectors/raferral/user';
import * as React from 'react';
import { EDealType } from '../../../../repositories/cpd/entities/v1_create_cpd_draft/PublicV1CreateCpdDraftRequest';
import { formatPhoneStringToInput, isPhoneValid } from '../../../../utils/formatPhoneString';
import { getRegions, sendForm } from '../../../../thunks/referral';
import { ChangeEvent } from 'react';
import { DEAL_TYPE_OPTIONS, IDealTypeOption, IRegionOption, mapRegionsListToOptions } from '../constants';
import { trackSendFormClick } from '../tracking';
import { selectReferralModalSource } from '../../../../selectors';
import { setReferralModalFormClose } from '../../../../slices';

export interface IFormProps {
  dealTypeValue?: EDealType[];
  dealTypeOptions: IDealTypeOption[];
  dealTypeChange: (value: EDealType) => void;
  phoneValue: string;
  onPhoneChange: (val: string) => void;
  regionValue?: number;
  regionOptions: IRegionOption[];
  onRegionChange: (val: number) => void;
  onSubmit: (source: 'referralLandingPageForm' | 'referralLandingPageConsultation') => void;
  legalAgreementAccepted: boolean;
  onLegalAgreementChange: (_: ChangeEvent<HTMLInputElement>, accepted: boolean) => void;
  source?: 'consultation' | 'ticket' | null;
  isDesktop?: boolean;
  errors?: {
    dealTypeError?: string;
    regionError?: string;
    phoneError?: string;
  };
}

export const useFormProps = (): IFormProps => {
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';

  const dispatch = useReferralAppDispatch();
  const toastApi = useToast({ placement: 'top-right' });

  const regionList = useSelector(selectRegionsList);
  const userPhone = useSelector(getReferralUserPhone);
  const source = useSelector(selectReferralModalSource);

  const [dealType, setDealType] = React.useState<EDealType | undefined>(undefined);
  const [region, setRegion] = React.useState<number | undefined>(undefined);
  const [phoneValue, setPhoneValue] = React.useState(formatPhoneStringToInput(userPhone));
  const [legalAgreementAccepted, setLegalAgreementAccepted] = React.useState<boolean>(false);

  const [errors, setErrors] = React.useState<IFormProps['errors']>(undefined);

  const handleDealTypeChange = React.useCallback(
    async (newDealType: EDealType) => {
      setErrors({
        ...errors,
        dealTypeError: undefined,
      });
      setDealType(newDealType);

      await dispatch(getRegions(newDealType));
    },
    [dispatch],
  );

  const handleRegionChange = React.useCallback((newRegion: number) => {
    setErrors({
      ...errors,
      regionError: undefined,
    });
    setRegion(newRegion);
  }, []);

  const handlePhoneChange = React.useCallback((value: string) => {
    setErrors({
      ...errors,
      phoneError: undefined,
    });
    setPhoneValue(value);
  }, []);

  const handleLegalAgreementChange = React.useCallback((_: ChangeEvent<HTMLInputElement>, accepted: boolean) => {
    setLegalAgreementAccepted(accepted);
  }, []);

  const handleSubmit = React.useCallback(
    async (source: 'referralLandingPageForm' | 'referralLandingPageConsultation') => {
      const isFormValid = (): boolean => {
        if (!dealType) {
          setErrors({
            ...errors,
            dealTypeError: 'Выберите тип услуги',
          });

          return false;
        }

        if (!region) {
          setErrors({
            ...errors,
            regionError: 'Выберите регион',
          });

          return false;
        }

        if (!isPhoneValid(phoneValue)) {
          setErrors({
            ...errors,
            phoneError: 'Введите корректный номер телефона',
          });

          return false;
        }

        return true;
      };

      if (isFormValid()) {
        setErrors(undefined);

        const res = await dispatch(
          sendForm({
            source,
            dealType: dealType as EDealType,
            regionId: region as number,
            phone: phoneValue,
            legalAgreementAccepted,
          }),
        );

        if (res.payload) {
          trackSendFormClick(res.payload as number);
          dispatch(setReferralModalFormClose());
          toastApi.open({ type: 'success', text: 'Скоро свяжемся с вами' });
        } else {
          toastApi.open({ type: 'error', text: 'Что-то пошло не так — попробуйте ещё раз' });
        }
      }
    },
    [dispatch, dealType, region, phoneValue],
  );

  return {
    dealTypeValue: dealType ? [dealType] : undefined,
    dealTypeOptions: DEAL_TYPE_OPTIONS,
    dealTypeChange: handleDealTypeChange,
    phoneValue,
    onPhoneChange: handlePhoneChange,
    regionValue: region,
    regionOptions: mapRegionsListToOptions(regionList),
    onRegionChange: handleRegionChange,
    onSubmit: handleSubmit,
    legalAgreementAccepted,
    onLegalAgreementChange: handleLegalAgreementChange,
    source,
    isDesktop,
    errors,
  };
};
